// src/components/login/admin/create-user-page.tsx
//
// Provision a user. This is the authorised way accounts get made: self-service
// sign-up is closed by default (Global Settings → Platform), so without this
// screen the only route was a hand-rolled API call.
//
// LAYOUT RULES, written down because this page was rebuilt to obey them:
//   * One column, one flow, one scrollbar — the document's. No element here
//     sets a viewport height, and nothing scrolls inside anything else. The
//     brand picker in particular is a wrapping list, NOT a fixed-height
//     scroll box, so the page has no nested scrollport at all.
//   * No overlays, portals or popovers. Every control is a plain input,
//     button or native radio in normal flow.
// A page of text inputs is also where browser extensions (LanguageTool,
// password managers, grammar checkers) inject their own positioned overlays.
// Keeping our own DOM flat means anything odd on this screen is theirs, and
// can be confirmed in one incognito reload rather than debugged in our code.
//
// Two backend calls, in order, because they are two different resources:
//
//   POST /auth/admin/create-user   the account          (required)
//   PUT  /brands/users/{id}        their brand access   (optional)
//
// They are NOT a transaction. If the brand assignment fails the account still
// exists, and the page says exactly that instead of a generic error that would
// leave an admin guessing whether to retry the whole thing — a retry would
// collide on the username and read as a second, different failure.
//
// The password set here is temporary. The backend flags must_change_password,
// so the user replaces it on first sign-in and then enrols in MFA. The created
// credentials are shown once, on the success step, for the admin to pass on.

import { useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  ArrowLeft,
  Check,
  Copy,
  Loader2,
  RefreshCcw,
  UserPlus,
} from "lucide-react";

import { authApi, type AdminCreatedUser } from "@/api/auth";
import { brandsApi, type AssignableBrand } from "@/api/brands";
import { Input } from "../../ui/input";
import { Button } from "../../kit";
import {
  PasswordRequirements,
  checkPassword,
  PASSWORD_MIN_LENGTH,
} from "../password-requirements";
import { USER_TYPES, DEFAULT_USER_TYPE } from "./user-types";

interface CreateUserPageProps {
  /** Back to the user list. */
  onCancel: () => void;
  /** Fired after a successful create so the list can reload behind us. */
  onCreated?: (user: AdminCreatedUser) => void;
}

// Straight from the CSPRNG rather than a wordlist: a throwaway credential the
// user replaces on first sign-in, so it only has to clear the policy and be
// awkward to guess.
const PASSWORD_ALPHABET =
  "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%^&*-_+=";

function generatePassword(length = 16): string {
  const bytes = new Uint32Array(length);
  crypto.getRandomValues(bytes);
  let out = "";
  for (let i = 0; i < length; i++) {
    out += PASSWORD_ALPHABET[bytes[i] % PASSWORD_ALPHABET.length];
  }
  // Guarantee every class the policy asks for rather than trusting the draw —
  // a generated password that fails validation is a confusing dead end.
  return checkPassword(out).allValid ? out : generatePassword(length);
}

const MAX_WIDTH = 720;

const label: React.CSSProperties = {
  display: "block",
  fontSize: 14,
  fontWeight: 600,
  color: "var(--foreground)",
  marginBottom: 6,
};

const hint: React.CSSProperties = {
  fontSize: 12,
  color: "var(--muted-foreground)",
  lineHeight: 1.5,
  overflowWrap: "anywhere",
};

function Section({ title, note, children }: {
  title: string;
  note?: string;
  children: React.ReactNode;
}) {
  return (
    <section style={{ marginTop: 24, maxWidth: MAX_WIDTH }}>
      <h3
        style={{
          fontSize: 14,
          fontWeight: 700,
          color: "var(--foreground)",
          margin: 0,
        }}
      >
        {title}
      </h3>
      {note && <p style={{ ...hint, marginTop: 4, marginBottom: 0 }}>{note}</p>}
      <div style={{ marginTop: 12 }}>{children}</div>
    </section>
  );
}

export function CreateUserPage({ onCancel, onCreated }: CreateUserPageProps) {
  const [username, setUsername] = useState("");
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [userType, setUserType] = useState<string>(DEFAULT_USER_TYPE);
  const [isAdmin, setIsAdmin] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(true);

  const [brands, setBrands] = useState<AssignableBrand[]>([]);
  const [brandQuery, setBrandQuery] = useState("");
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [brandsLoading, setBrandsLoading] = useState(true);
  const [brandsError, setBrandsError] = useState("");

  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [created, setCreated] = useState<AdminCreatedUser | null>(null);
  const [assignedBrands, setAssignedBrands] = useState<string[]>([]);
  const [brandWarning, setBrandWarning] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    brandsApi
      .assignable()
      .then((list) => alive && setBrands(list))
      .catch((e: any) => alive && setBrandsError(String(e?.message || e)))
      .finally(() => alive && setBrandsLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const visibleBrands = useMemo(() => {
    const q = brandQuery.trim().toLowerCase();
    if (!q) return brands;
    return brands.filter((b) => b.brand_name.toLowerCase().includes(q));
  }, [brands, brandQuery]);

  const cleanUsername = username.trim().toLowerCase();
  const canSubmit =
    !!cleanUsername && checkPassword(password).allValid && !saving;

  function toggleBrand(name: string) {
    setSelectedBrands((prev) =>
      prev.includes(name) ? prev.filter((b) => b !== name) : [...prev, name],
    );
  }

  function pickUserType(value: string) {
    setUserType(value);
    // Choosing the Admin role ticks the admin flag, since that is what the role
    // means. Unticking it afterwards is still allowed — user_type drives the
    // nav, is_admin drives the admin endpoints, separate columns on purpose.
    if (value === "admin") setIsAdmin(true);
  }

  function startAnother() {
    setUsername("");
    setFullName("");
    setPassword("");
    setUserType(DEFAULT_USER_TYPE);
    setIsAdmin(false);
    setMfaRequired(true);
    setBrandQuery("");
    setSelectedBrands([]);
    setErr("");
    setCreated(null);
    setAssignedBrands([]);
    setBrandWarning("");
    setCopied(false);
  }

  async function onSubmit() {
    if (!canSubmit) return;
    setSaving(true);
    setErr("");
    setBrandWarning("");

    let user: AdminCreatedUser;
    try {
      user = await authApi.createUser({
        username: cleanUsername,
        password,
        full_name: fullName.trim() || null,
        user_type: userType || DEFAULT_USER_TYPE,
        is_admin: isAdmin,
        mfa_required: mfaRequired,
      });
    } catch (e: any) {
      setErr(e?.message || "Failed to create the user.");
      setSaving(false);
      return;
    }

    // The account exists from here on. A brand failure is a follow-up task,
    // never "creating the user failed".
    let assigned: string[] = [];
    if (selectedBrands.length) {
      try {
        await brandsApi.setUserBrands(user.id, selectedBrands);
        assigned = selectedBrands;
      } catch (e: any) {
        setBrandWarning(
          `The account was created, but assigning brands failed (${
            e?.message || e
          }). Assign them in Global Settings → Associate Brands.`,
        );
      }
    }

    setAssignedBrands(assigned);
    setCreated(user);
    setSaving(false);
    onCreated?.(user);
  }

  async function copyCredentials() {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(
        `Sign in: ${created.username}\nTemporary password: ${password}`,
      );
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked — the values are on screen to copy by hand */
    }
  }

  const backLink = (
    <button
      type="button"
      onClick={onCancel}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        background: "none",
        border: "none",
        cursor: "pointer",
        color: "var(--signal-blue)",
        fontWeight: 700,
        padding: 0,
        marginBottom: 12,
      }}
    >
      <ArrowLeft style={{ width: 16, height: 16 }} />
      Back to user list
    </button>
  );

  // ── Success step ─────────────────────────────────────────────────────────
  if (created) {
    return (
      <div style={{ marginTop: 16, maxWidth: MAX_WIDTH }}>
        {backLink}

        <h2 style={{ fontSize: 18, fontWeight: 800, margin: 0 }}>User created</h2>
        <p style={{ ...hint, marginTop: 4 }}>
          Pass these details on. The password is shown only once.
        </p>

        {brandWarning && (
          <p
            style={{
              ...hint,
              marginTop: 14,
              padding: "10px 12px",
              borderRadius: 8,
              color: "var(--foreground)",
              border: "1px solid var(--border)",
              backgroundColor: "var(--muted)",
            }}
          >
            {brandWarning}
          </p>
        )}

        <dl
          style={{
            marginTop: 14,
            padding: 14,
            borderRadius: 10,
            border: "1px solid var(--border)",
            backgroundColor: "var(--muted)",
            fontSize: 14,
            lineHeight: 1.9,
            overflowWrap: "anywhere",
          }}
        >
          <div>
            <strong>Sign in:</strong> {created.username}
          </div>
          <div>
            <strong>Temporary password:</strong>{" "}
            <code style={{ fontFamily: "var(--font-mono, monospace)" }}>{password}</code>
          </div>
          <div>
            <strong>Role:</strong>{" "}
            {USER_TYPES.find((t) => t.value === created.user_type)?.label ||
              created.user_type}
            {created.is_admin ? " · Administrator" : ""}
          </div>
          <div>
            <strong>Brands:</strong>{" "}
            {brandWarning
              ? "not assigned — see the note above"
              : assignedBrands.length
              ? assignedBrands.join(", ")
              : "none yet"}
          </div>
        </dl>

        <p style={{ ...hint, marginTop: 12 }}>
          On first sign-in they must set their own password
          {created.mfa_required ? ", then enrol in two-factor authentication" : ""}.
          {!assignedBrands.length && !created.is_admin
            ? " They will see no data until you assign a brand in Associate Brands."
            : ""}
        </p>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 18 }}>
          <Button variant="secondary" icon={copied ? Check : Copy} onClick={copyCredentials}>
            {copied ? "Copied" : "Copy details"}
          </Button>
          <Button variant="secondary" icon={UserPlus} onClick={startAnother}>
            Add another
          </Button>
          <Button onClick={onCancel}>Back to user list</Button>
        </div>
      </div>
    );
  }

  // ── Form ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ marginTop: 16 }}>
      {backLink}

      <h2 style={{ fontSize: 18, fontWeight: 800, margin: 0 }}>Add user</h2>
      <p style={{ ...hint, marginTop: 4, maxWidth: MAX_WIDTH }}>
        Creates the account directly — there is no invitation email. The password you
        set is temporary: they replace it on first sign-in.
      </p>

      {err && (
        <p
          style={{
            marginTop: 14,
            maxWidth: MAX_WIDTH,
            padding: "10px 12px",
            borderRadius: 8,
            fontSize: 14,
            display: "flex",
            alignItems: "flex-start",
            gap: 8,
            color: "var(--infrared-coral)",
            border: "1px solid var(--infrared-coral)",
            backgroundColor: "var(--infrared-coral-5)",
          }}
        >
          <AlertCircle style={{ width: 16, height: 16, flexShrink: 0, marginTop: 2 }} />
          <span>{err}</span>
        </p>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
      >
        {/* ── Account ────────────────────────────────────────────────── */}
        <Section title="Account">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label style={label} htmlFor="cu-username">
                Email (username)
              </label>
              <Input
                id="cu-username"
                type="email"
                value={username}
                autoComplete="off"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                placeholder="person@company.com"
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>
            <div>
              <label style={label} htmlFor="cu-fullname">
                Full name <span style={{ fontWeight: 400 }}>(optional)</span>
              </label>
              <Input
                id="cu-fullname"
                value={fullName}
                autoComplete="off"
                spellCheck={false}
                placeholder="Alex Rivera"
                onChange={(e) => setFullName(e.target.value)}
              />
            </div>
          </div>
        </Section>

        {/* ── Password ───────────────────────────────────────────────── */}
        <Section
          title="Temporary password"
          note="Give this to the user. They must replace it the first time they sign in."
        >
          <div style={{ display: "flex", gap: 8 }}>
            <Input
              id="cu-password"
              value={password}
              autoComplete="new-password"
              spellCheck={false}
              placeholder={`At least ${PASSWORD_MIN_LENGTH} characters`}
              onChange={(e) => setPassword(e.target.value)}
              style={{ flex: 1, fontFamily: "var(--font-mono, monospace)" }}
            />
            <Button
              type="button"
              variant="secondary"
              icon={RefreshCcw}
              onClick={() => setPassword(generatePassword())}
              style={{ flexShrink: 0 }}
            >
              Generate
            </Button>
          </div>
          <div style={{ marginTop: 10 }}>
            <PasswordRequirements password={password} />
          </div>
        </Section>

        {/* ── Role ───────────────────────────────────────────────────── */}
        <Section
          title="Role"
          note="Controls which sections of the app this user can access."
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {USER_TYPES.map(({ value, label: roleLabel, description }) => {
              const active = userType === value;
              return (
                <label
                  key={value}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 12,
                    padding: "12px 14px",
                    borderRadius: 10,
                    cursor: "pointer",
                    border: `1px solid ${active ? "var(--alpha-teal)" : "var(--border)"}`,
                    backgroundColor: active ? "var(--alpha-teal-10)" : "var(--muted)",
                  }}
                >
                  <input
                    type="radio"
                    name="cu_user_type"
                    value={value}
                    checked={active}
                    onChange={() => pickUserType(value)}
                    style={{
                      accentColor: "var(--alpha-teal)",
                      width: 16,
                      height: 16,
                      flexShrink: 0,
                      marginTop: 3,
                    }}
                  />
                  <span style={{ minWidth: 0 }}>
                    <span
                      style={{
                        display: "block",
                        fontWeight: 600,
                        fontSize: 14,
                        color: active ? "var(--alpha-teal)" : "var(--foreground)",
                      }}
                    >
                      {roleLabel}
                    </span>
                    <span style={{ ...hint, display: "block", marginTop: 2 }}>
                      {description}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>

          <label
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 10,
              marginTop: 14,
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={isAdmin}
              onChange={(e) => setIsAdmin(e.target.checked)}
              style={{
                accentColor: "var(--alpha-teal)",
                width: 16,
                height: 16,
                flexShrink: 0,
                marginTop: 2,
              }}
            />
            <span style={{ minWidth: 0 }}>
              <span style={{ display: "block", fontSize: 14 }}>Admin user</span>
              <span style={{ ...hint, display: "block", marginTop: 2 }}>
                Grants User Management and every other admin screen — including this
                one, and the switch that opens public sign-up.
              </span>
            </span>
          </label>

          <label
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 10,
              marginTop: 12,
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={mfaRequired}
              onChange={(e) => setMfaRequired(e.target.checked)}
              style={{
                accentColor: "var(--alpha-teal)",
                width: 16,
                height: 16,
                flexShrink: 0,
                marginTop: 2,
              }}
            />
            <span style={{ minWidth: 0 }}>
              <span style={{ display: "block", fontSize: 14 }}>
                Require two-factor authentication
              </span>
              <span style={{ ...hint, display: "block", marginTop: 2 }}>
                They set up an authenticator app before they can use the account.
              </span>
            </span>
          </label>
        </Section>

        {/* ── Brand access ───────────────────────────────────────────── */}
        <Section
          title="Brand access (optional)"
          note="Non-admins only see brands assigned to them, so an account with none signs in to an empty app. You can also do this later in Associate Brands."
        >
          {brandsError ? (
            <p style={{ ...hint, color: "var(--infrared-coral)" }}>
              Could not load brands ({brandsError}). You can still create the account
              and assign brands afterwards.
            </p>
          ) : brandsLoading ? (
            <p style={{ ...hint, display: "flex", alignItems: "center", gap: 8 }}>
              <Loader2 style={{ width: 15, height: 15 }} className="animate-spin" />
              Loading brands…
            </p>
          ) : brands.length === 0 ? (
            <p style={hint}>No brands to assign yet.</p>
          ) : (
            <>
              <Input
                value={brandQuery}
                placeholder="Filter brands…"
                spellCheck={false}
                onChange={(e) => setBrandQuery(e.target.value)}
                style={{ maxWidth: 320 }}
              />

              {/* A wrapping list, deliberately not a fixed-height scroll box:
                  this page has no nested scrollport, so the only scrollbar on
                  screen is the document's. */}
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 8,
                  marginTop: 12,
                }}
              >
                {visibleBrands.length === 0 ? (
                  <p style={hint}>No brands match that filter.</p>
                ) : (
                  visibleBrands.map((b) => {
                    const checked = selectedBrands.includes(b.brand_name);
                    return (
                      <button
                        key={b.brand_name}
                        type="button"
                        aria-pressed={checked}
                        onClick={() => toggleBrand(b.brand_name)}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          padding: "7px 12px",
                          borderRadius: 999,
                          cursor: "pointer",
                          fontSize: 13,
                          fontWeight: checked ? 600 : 400,
                          border: `1px solid ${checked ? "var(--alpha-teal)" : "var(--border)"}`,
                          backgroundColor: checked
                            ? "var(--alpha-teal-10)"
                            : "var(--muted)",
                          color: checked ? "var(--alpha-teal)" : "var(--foreground)",
                        }}
                      >
                        {checked && <Check style={{ width: 13, height: 13 }} />}
                        {b.brand_name}
                      </button>
                    );
                  })
                )}
              </div>

              {selectedBrands.length > 0 && (
                <p style={{ ...hint, marginTop: 10 }}>
                  {selectedBrands.length} brand
                  {selectedBrands.length === 1 ? "" : "s"} selected:{" "}
                  {selectedBrands.join(", ")}
                </p>
              )}
            </>
          )}
        </Section>

        {/* ── Actions ────────────────────────────────────────────────── */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            marginTop: 28,
            maxWidth: MAX_WIDTH,
            borderTop: "1px solid var(--border)",
            paddingTop: 16,
          }}
        >
          <Button type="submit" icon={UserPlus} disabled={!canSubmit} loading={saving}>
            Create user
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}

export default CreateUserPage;
